import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  Paper,
  Snackbar,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import emailjs from '@emailjs/browser';
import { Description, LocalShipping, Calculate } from '@mui/icons-material';



const serviceTypes = [
  { value: 'maritime', label: 'Groupage Maritime' },
  { value: 'air', label: 'Groupage Aérien' },
  { value: 'moving', label: 'Déménagement International' },
  { value: 'shopping', label: 'Achat International' },
];

const validationSchema = Yup.object().shape({
  serviceType: Yup.string().required('Veuillez sélectionner un service'),
  firstName: Yup.string().required('Le prénom est requis'),
  lastName: Yup.string().required('Le nom est requis'),
  email: Yup.string().email('Email invalide').required('L\'email est requis'),
  phone: Yup.string().required('Le numéro de téléphone est requis'),
  company: Yup.string(),
  origin: Yup.string().required('Le lieu de départ est requis'),
  destination: Yup.string().required('La destination est requise'),
  description: Yup.string().required('La description est requise'),
  cargoType: Yup.string().required('Le type de marchandise est requis'),
  weight: Yup.number()
    .typeError('Le poids doit être un nombre')
    .positive('Le poids doit être positif')
    .required('Le poids est requis'),
  volume: Yup.number()
    .typeError('Le volume doit être un nombre')
    .positive('Le volume doit être positif'),
  preferredContact: Yup.string().required('Veuillez sélectionner un mode de contact préféré'),
});

const initialValues = {
  serviceType: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  origin: '',
  destination: '',
  description: '',
  cargoType: '',
  weight: '',
  volume: '',
  preferredContact: 'email',
};

const Quote = () => {
  // Initialisation d'EmailJS
  emailjs.init('fdoyj3Xefl5bT4Xhs');
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Préparation des paramètres du template
      const templateParams = {
        user_name: `${values.firstName} ${values.lastName}`,
        user_email: values.email,
        user_phone: values.phone,
        company: values.company || 'Non spécifié',
        service_type: values.serviceType,
        origin: values.origin,
        destination: values.destination,
        cargo_type: values.cargoType,
        weight: values.weight,
        volume: values.volume || 'Non spécifié',
        description: values.description,
        preferred_contact: values.preferredContact,
        to_email: 'contact@aft-import-export.net',
        reply_to: values.email
      };

      // Envoi via EmailJS
      const response = await emailjs.send(
        'service_hi07lrh',
        'template_tbdz8om',
        templateParams
      );
      
      console.log('EmailJS Success:', response);
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Votre demande de devis a été envoyée avec succès.',
      });
      resetForm();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: `Une erreur est survenue lors de l'envoi du devis: ${error.message}`,
      });
    }
    setSubmitting(false);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 8,
          mb: 6,
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom>
            Demande de Devis
          </Typography>
          <Typography variant="h5">
            Obtenez un devis personnalisé pour vos besoins en transport et logistique
          </Typography>
        </Container>
      </Box>

      {/* Features */}
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <Description sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Simple et Rapide
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Remplissez le formulaire en quelques minutes
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <LocalShipping sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Services Adaptés
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Solutions sur mesure pour vos besoins
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <Calculate sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Devis Détaillé
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Recevez une estimation précise
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Form */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, isSubmitting }) => (
              <Form>
                <Grid container spacing={3}>
                  {/* Service Type */}
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label="Type de Service"
                      name="serviceType"
                      value={values.serviceType}
                      onChange={handleChange}
                      error={touched.serviceType && Boolean(errors.serviceType)}
                      helperText={touched.serviceType && errors.serviceType}
                    >
                      {serviceTypes.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Personal Information */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Prénom"
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nom"
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Téléphone"
                      name="phone"
                      value={values.phone}
                      onChange={handleChange}
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Entreprise (optionnel)"
                      name="company"
                      value={values.company}
                      onChange={handleChange}
                    />
                  </Grid>

                  {/* Shipment Details */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Lieu de départ"
                      name="origin"
                      value={values.origin}
                      onChange={handleChange}
                      error={touched.origin && Boolean(errors.origin)}
                      helperText={touched.origin && errors.origin}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Destination"
                      name="destination"
                      value={values.destination}
                      onChange={handleChange}
                      error={touched.destination && Boolean(errors.destination)}
                      helperText={touched.destination && errors.destination}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Description de la marchandise"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Type de marchandise"
                      name="cargoType"
                      value={values.cargoType}
                      onChange={handleChange}
                      error={touched.cargoType && Boolean(errors.cargoType)}
                      helperText={touched.cargoType && errors.cargoType}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Poids (kg)"
                      name="weight"
                      type="number"
                      value={values.weight}
                      onChange={handleChange}
                      error={touched.weight && Boolean(errors.weight)}
                      helperText={touched.weight && errors.weight}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Volume (m³)"
                      name="volume"
                      type="number"
                      value={values.volume}
                      onChange={handleChange}
                      error={touched.volume && Boolean(errors.volume)}
                      helperText={touched.volume && errors.volume}
                    />
                  </Grid>

                  {/* Contact Preference */}
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Mode de contact préféré</FormLabel>
                      <RadioGroup
                        row
                        name="preferredContact"
                        value={values.preferredContact}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value="email"
                          control={<Radio />}
                          label="Email"
                        />
                        <FormControlLabel
                          value="phone"
                          control={<Radio />}
                          label="Téléphone"
                        />
                        <FormControlLabel
                          value="both"
                          control={<Radio />}
                          label="Les deux"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande de devis'}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Paper>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Quote;
